import React from 'react';

import { Box, Collapse, HStack, Stack, Text } from '@chakra-ui/react';
import { ChevronRightIcon, IconBadge } from '@saas-ui/react';
import { Check, ChevronDown } from 'lucide-react';

import { Card, CardHeader, CardTitle, IconButton } from '@/components/core';
import { FONTS_OPACITY } from '@/constant';
import { detailInteractiveCardSx } from '@/styles/variants/detailSurface';

export interface ExpandableJobCardProps {
  title: string;
  sublabel?: string | null;
  statusLabel?: string | null;
  statusColor?: string;
  dateLabel?: string | null;
  engineerName?: string | null;
  summary?: string | null;
  matchPercent?: number | null;
  icon: React.ReactElement;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onOpenJob: () => void;
  openJobAriaLabel: string;
  showSelection?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  selectAriaLabel?: string;
  deselectAriaLabel?: string;
  headerActions?: React.ReactNode;
  showExpandHint?: boolean;
  children?: React.ReactNode;
  interactiveBackground?: boolean;
  'data-cy'?: string;
}

const StatusMiddot = () => (
  <Text fontSize='xs' lineHeight='5' opacity={0.78} color='fonts.primary'>
    ·
  </Text>
);

export const ExpandableJobCard: React.FC<ExpandableJobCardProps> = ({
  title,
  sublabel,
  statusLabel,
  statusColor = 'tags.blue.font',
  dateLabel,
  engineerName,
  summary,
  matchPercent,
  icon,
  isExpanded,
  onToggleExpand,
  onOpenJob,
  openJobAriaLabel,
  showSelection = false,
  isSelected = false,
  onToggleSelect,
  selectAriaLabel,
  deselectAriaLabel,
  headerActions,
  showExpandHint = true,
  children,
  interactiveBackground = false,
  'data-cy': dataCy = 'global-search-outcome',
}) => {
  const hasStatusLine = Boolean(statusLabel || dateLabel || engineerName);

  const handleCardClick = () => {
    onToggleExpand();
  };

  const handleToggleExpand = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();
    event.preventDefault();
    onToggleExpand();
  };

  const handleToggleSelect = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();
    event.preventDefault();
    onToggleSelect?.();
  };

  return (
    <Card
      px={0}
      py={{ base: 3, md: 4 }}
      rounded='2xl'
      color='white'
      cursor='pointer'
      bg='cards.bg'
      border='1px solid'
      borderColor={isSelected ? 'tabs.font-selected' : 'border.light'}
      overflow='hidden'
      className='group'
      data-cy={dataCy}
      aria-expanded={isExpanded}
      onClick={handleCardClick}
      sx={
        interactiveBackground
          ? {
              ...detailInteractiveCardSx,
              _hover: {
                bg: 'cards.bg-hover',
                borderColor: isSelected
                  ? 'tabs.font-selected'
                  : 'border.light',
              },
            }
          : undefined
      }
    >
      <CardHeader
        py={0}
        px={{ base: 3, md: 4 }}
        action={
          <HStack gap={1} flexShrink={0}>
            {headerActions}
            <IconButton
              icon={<ChevronRightIcon boxSize='16px' />}
              aria-label={openJobAriaLabel}
              boxSize={{ base: '32px', md: '36px' }}
              variant='glassy'
              flexShrink={0}
              _groupHover={{
                bg: 'glass-items.bg-hover',
              }}
              onClick={event => {
                event.stopPropagation();
                onOpenJob();
              }}
            />
          </HStack>
        }
      >
        <HStack align='flex-start' gap={{ base: 2, md: 2.5 }} w='full' minW={0}>
          {showSelection ? (
            <Box
              as='button'
              type='button'
              flexShrink={0}
              mt={2}
              boxSize='20px'
              rounded='4px'
              border='1px solid'
              borderColor={isSelected ? 'tabs.font-selected' : 'border.light'}
              bg={isSelected ? 'tabs.font-selected' : 'transparent'}
              display='inline-flex'
              alignItems='center'
              justifyContent='center'
              aria-label={isSelected ? deselectAriaLabel : selectAriaLabel}
              aria-pressed={isSelected}
              data-cy='advanced-search-outcome-checkbox'
              onClick={handleToggleSelect}
            >
              {isSelected ? <Check size={12} color='white' /> : null}
            </Box>
          ) : null}
          <Box flexShrink={0} display={{ base: 'none', sm: 'block' }}>
            <IconBadge
              icon={icon}
              variant='solid'
              colorScheme='whiteAlpha'
              isRound
              boxSize='36px'
              backgroundColor='glass-items.bg-active'
              _groupHover={{
                backgroundColor: 'cards.bg-hover',
              }}
            />
          </Box>
          <Stack flex={1} gap={0} minW={0} maxW='100%'>
            <CardTitle
              fontSize='sm'
              lineHeight='5'
              fontWeight='normal'
              noOfLines={1}
            >
              {title}
            </CardTitle>
            {sublabel ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={0.5}
                noOfLines={{ base: 2, xl: 1 }}
                color='fonts.primary'
              >
                {sublabel}
              </Text>
            ) : null}
            {hasStatusLine ? (
              <HStack gap={1.5} minW={0} flexWrap='wrap'>
                {statusLabel ? (
                  <>
                    <Box
                      w={1.5}
                      h={1.5}
                      rounded='full'
                      bg={statusColor}
                      flexShrink={0}
                    />
                    <Text
                      fontSize='xs'
                      lineHeight='5'
                      fontWeight='semibold'
                      color={statusColor}
                      textTransform='uppercase'
                      noOfLines={1}
                    >
                      {statusLabel}
                    </Text>
                  </>
                ) : null}
                {statusLabel && dateLabel ? <StatusMiddot /> : null}
                {dateLabel ? (
                  <Text
                    fontSize='xs'
                    lineHeight='5'
                    opacity={0.55}
                    color='fonts.primary'
                    noOfLines={1}
                    data-cy='global-search-outcome-date'
                  >
                    {dateLabel}
                  </Text>
                ) : null}
                {(statusLabel || dateLabel) && engineerName ? (
                  <StatusMiddot />
                ) : null}
                {engineerName ? (
                  <Text
                    fontSize='xs'
                    lineHeight='5'
                    opacity={0.55}
                    color='fonts.primary'
                    noOfLines={1}
                  >
                    {engineerName}
                  </Text>
                ) : null}
              </HStack>
            ) : null}
            {summary ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={FONTS_OPACITY}
                noOfLines={isExpanded ? undefined : 2}
                color='fonts.primary'
                mt={1}
              >
                {summary}
              </Text>
            ) : null}
            {matchPercent != null ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={0.45}
                color='fonts.primary'
                mt={1}
              >
                {matchPercent}% match
              </Text>
            ) : null}

            {showExpandHint ? (
              <HStack mt={2} gap={2}>
                <Text
                  as='button'
                  type='button'
                  fontSize='xs'
                  lineHeight='4'
                  fontWeight='semibold'
                  color='tabs.font-selected'
                  cursor='pointer'
                  display='inline-flex'
                  alignItems='center'
                  gap={1}
                  onClick={handleToggleExpand}
                  aria-expanded={isExpanded}
                  data-cy='global-search-outcome-expand'
                >
                  {isExpanded ? 'Hide details' : 'Show details'}
                  <Box
                    as='span'
                    display='inline-flex'
                    transform={isExpanded ? 'rotate(180deg)' : 'none'}
                    transition='transform 0.2s ease'
                  >
                    <ChevronDown size={14} />
                  </Box>
                </Text>
              </HStack>
            ) : null}

            <Box
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <Collapse in={isExpanded} animateOpacity style={{ minWidth: 0 }}>
                {children}
              </Collapse>
            </Box>
          </Stack>
        </HStack>
      </CardHeader>
    </Card>
  );
};
